/**
 * File components/ChoicesForm.js
 *
 * To make choices for reservation: 
 *
 * Learn more: https://saint-augustin.ch
 */

import {useState, useRef, useEffect} from 'react';
import {useBookingDataContext} from '../context/BookingDataProvider';
import DatetimesInterval from "./DatetimesInterval";
import Locations from "./Locations";

const ChoicesForm = () => {
	const {
		beginBooking, setBeginBooking,
		endBooking, setEndBooking,
		locations,
		events,
		nbPersons, setNbPersons,
		disponibilityStatus, setDisponibilityStatus
	} = useBookingDataContext();

	const [errorNbPersons, setErrorNbPersons] = useState(null);
	const [errorDisponibility, setErrorDisponibility] = useState("Disponibilité non encore vérifiée.");
	const [successDisponibility, setSuccessDisponibility] = useState(null);
	const [errorDatetimesInterval, setErrorDatetimesInterval] = useState(null);

	/* ------------------------------
     FORM HANDLERS
  ------------------------------ */
const handleInputNbPersonsChange = (value) => {
	value = parseInt(value, 10);
	if (isNaN(value) || value < 1) {
		setErrorNbPersons("Veuillez saisir au moins 1 personne.");
	} else {
		setErrorNbPersons(null);
		setNbPersons(value);
	}
	setDisponibilityStatus(false);
	setSuccessDisponibility(null);
	setErrorDatetimesInterval("Disponibilité non encore vérifiée.");
};

function formatDateForInput(date) {
	const pad = (n) => n.toString().padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const handleInputBeginBookingChange = (value) => {
    const newDate = correctDate(value);
    setBeginBooking(formatDateForInput(newDate));
    setDisponibilityStatus(false);
	setSuccessDisponibility(null);
	setErrorDisponibility("Disponibilité non encore vérifiée.");
};

const handleInputEndBookingChange = (value) => {
    const newDate = correctDate(value);
    setEndBooking(formatDateForInput(newDate));
    setDisponibilityStatus(false);
	setSuccessDisponibility(null);
	setErrorDisponibility("Disponibilité non encore vérifiée.");
};

/* ------------------------------
     CHECK AVAILABILITY
  ------------------------------ */
function isAvailable(events, start, end) {
	const s = start.getTime();
	const e = end.getTime();

	return !events.some(ev => {
		const evS = new Date(ev.start).getTime();
		const evE = new Date(ev.end).getTime();

		return s < evE && e > evS; // collision ?
	});
	//return !events.some(ev => start < ev.end && end > ev.start);
}

function handleVerifyDisponibility(e) {
	console.log("handleVerifyDisponibility");
    e.preventDefault();
    setErrorDisponibility("");
    setSuccessDisponibility("");

    const selected = locations.filter(l => l.isSelected);
    if (selected.length === 0) {
      setErrorDisponibility("Veuillez sélectionner au moins un lieu.");
      return;
    }

    const start = new Date(beginBooking);
    const end = new Date(endBooking);

    const relatedEvents = events.filter(ev =>
      selected.some(loc => loc.id === ev.locationId)
    );
	
    const allFree = selected.every(loc =>
		isAvailable(
			relatedEvents.filter(ev => ev.locationId === loc.id),
			start,
			end
		)
    );

    if (allFree) {
		setDisponibilityStatus(true);
		setSuccessDisponibility("Les lieux que vous avez choisis sont bien disponibles pour ce créneau horaire.");
    } else {
		setDisponibilityStatus(false);
		setErrorDisponibility("Un ou plusieurs lieux sont déjà occupés sur ce créneau horaire.");
    }
}


  /* ------------------------------
     DATE HELPERS
  ------------------------------ */

  function correctDate(date) {
    const { minHour, minMinutes, maxHour, maxMinutes } = timeLimitsRef.current;
    const newDate = new Date(date);
    const hours = newDate.getHours();
    const minutes = newDate.getMinutes();

    setErrorDatetimesInterval(false);

    if (hours < minHour || (hours === minHour && minutes < minMinutes)) {
      newDate.setHours(minHour, minMinutes, 0, 0);
      setErrorDatetimesInterval("La date a été ajustée selon les disponibilités minimales.");
    }

    if (hours > maxHour || (hours === maxHour && minutes > maxMinutes)) {
      newDate.setHours(maxHour, maxMinutes, 0, 0);
      setErrorDatetimesInterval("La date a été ajustée selon les disponibilités maximales.");
    }

    return newDate;
  }

  const timeLimitsRef = useRef({
    minHour: 5,
    minMinutes: 0,
    maxHour: 23,
    maxMinutes: 59
  });

	/* ------------------------------
     TIME RANGE CALCULATION
  ------------------------------ */
	useEffect(() => {
		const selected = locations?.filter(loc => loc.isSelected) || [];

		if (selected.length === 0) return;

		const toMinutes = (timeStr) => {
		if (!timeStr) return 0;
		const [h, m] = timeStr.split(":").map(Number);
		return h * 60 + m;
		};

		const allMinTimes = selected.map(l => l.min_time_available).filter(Boolean).map(toMinutes);
		const allMaxTimes = selected.map(l => l.max_time_available).filter(Boolean).map(toMinutes);

		if (!allMinTimes.length || !allMaxTimes.length) return;

		const maxFromMin = Math.max(...allMinTimes);
		const minFromMax = Math.min(...allMaxTimes);

		timeLimitsRef.current = {
		minHour: Math.floor(maxFromMin / 60),
		minMinutes: maxFromMin % 60,
		maxHour: Math.floor(minFromMax / 60),
		maxMinutes: minFromMax % 60
		};
	}, [locations.map(l => `${l.isSelected}-${l.min_time_available}-${l.max_time_available}`).join(",")]);

	return (
		<form onSubmit={handleVerifyDisponibility}>
			<DatetimesInterval
				id="datetimesInterval"
				startDatetime={beginBooking}
				endDatetime={endBooking}
				onStartChange={handleInputBeginBookingChange}
				onEndChange={handleInputEndBookingChange}
			/>
			{<div className="text-danger">{errorDatetimesInterval}</div>}
			
			<div className="form-group">
				<label><strong>Nombre de personnes&nbsp;:</strong></label>
				<input
					type="number"
					min="1"
					value={nbPersons}
					onChange={(e) => handleInputNbPersonsChange(e.target.value)}
				/>
				{errorNbPersons && <div className="text-danger">{errorNbPersons}</div>}
			</div>

			<Locations />

			{errorDisponibility && 
				<div className="error"><br/><strong>{errorDisponibility}</strong><br/><br/></div>
			}
			{successDisponibility && 
				<div className="success"><br/><strong>{successDisponibility}</strong><br/><br/></div>
			}

			<input type="submit" value="Vérifier la disponibilité" />
		</form>
	);
};

export default ChoicesForm;